import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Tab,
  Tabs,
  Paper,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useAuth } from "../../firebase";
import { useSnackbar } from "notistack";
import {
  fetchAllOrders,
  getOrderStatistics,
  OrderWithClient,
} from "../../utils/adminOrderUtils";
import OrdersTab from "./OrdersTab";
import ClientsTab from "./ClientsTab";
import DashboardOverview from "./DashboardOverview";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    "aria-controls": `admin-tabpanel-${index}`,
  };
}

const AdminDashboard: React.FC = () => {
  const { user, isAdmin, adminConfig } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<OrderWithClient[]>([]);
  const [statistics, setStatistics] = useState<any>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      console.log("🔄 Loading dashboard data from Firestore...");

      // Load orders and statistics
      const [ordersData, statsData] = await Promise.all([
        fetchAllOrders(),
        getOrderStatistics(),
      ]);

      console.log(
        `📊 Loaded ${ordersData.length} orders from Firestore:`,
        ordersData
      );
      console.log("📈 Statistics:", statsData);

      setOrders(ordersData);
      setStatistics(statsData);

      enqueueSnackbar(`Dashboard loaded: ${ordersData.length} orders found`, {
        variant: "success",
      });
    } catch (error) {
      console.error("❌ Error loading dashboard data:", error);
      enqueueSnackbar("Error loading dashboard data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Access denied. You do not have admin privileges.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome back, {user?.displayName || user?.email}
        </Typography>
        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
          <Chip label="Admin" color="primary" variant="outlined" size="small" />
          {adminConfig?.permissions?.map((permission) => (
            <Chip
              key={permission.resource}
              label={permission.resource}
              color="secondary"
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2, alignSelf: "center" }}>
            Loading data from Firestore...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Debug Info */}
          <Box sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              📊 Dashboard Status: {orders.length} orders loaded from Firestore
            </Typography>
          </Box>

          {/* Tabs */}
          <Paper sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="admin dashboard tabs"
              >
                <Tab label="Overview" {...a11yProps(0)} />
                <Tab label="Orders" {...a11yProps(1)} />
                <Tab label="Clients" {...a11yProps(2)} />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <DashboardOverview
                statistics={statistics}
                orders={orders}
                onRefresh={loadDashboardData}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <OrdersTab
                orders={orders}
                onRefresh={loadDashboardData}
                onOrderUpdate={(updatedOrder: OrderWithClient) => {
                  setOrders((prev) =>
                    prev.map((order) =>
                      order.id === updatedOrder.id ? updatedOrder : order
                    )
                  );
                }}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <ClientsTab orders={orders} onRefresh={loadDashboardData} />
            </TabPanel>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default AdminDashboard;
